import { useState, useEffect, useCallback, useRef } from "react";

import { QUESTION_TIME_LIMIT } from "../../constants/game";
import { useConversionFunnel } from "../../features/monetization/hooks/useConversionFunnel";
import { useLanguageStore } from "../../features/quiz-core/languageStore";
import { UserAnswer } from "../../types/questionFormats";
import { sessionQuestionService } from "../services/sessionQuestionService";
import { supabase, fetchQuestions } from "../services/supabaseService";

import {
  evaluateSinglePlayerAnswer,
  normalizeStoredSinglePlayerAnswer,
  StoredSinglePlayerAnswer,
} from "./singlePlayerScoring";

interface UseSinglePlayerQuizControllerArgs {
  category: string;
  categoryId: number;
  questionCount: number;
  difficulty_id: string | number;
  onQuizEnd?: (result: { score: number; total: number }) => void;
  initialQuestions?: QuizQuestion[];
  sessionId?: string;
}

type QuizOption = string | { id: string; text: string };

interface QuizQuestion {
  id: number;
  question: string;
  options: QuizOption[];
  answer: string;
  question_type?: string;
  format_data?: Record<string, unknown>;
  correct_answer?: string;
}

/** What the player did on one question; feeds achievement format stats at submit time. */
export interface QuestionOutcome {
  question_type?: string;
  isCorrect: boolean;
}

interface UseSinglePlayerQuizControllerResult {
  questions: QuizQuestion[];
  /** Indexed like `answers`; sparse until every question is answered. */
  questionOutcomes: QuestionOutcome[];
  /** Seconds since the questions were shown, or null before they load. */
  getElapsedSeconds: () => number | null;
  currentQuestion: QuizQuestion | undefined;
  currentIndex: number;
  totalQuestions: number;
  answers: StoredSinglePlayerAnswer[];
  loading: boolean;
  score: number;
  correctCount: number;
  finished: boolean;
  error: string | null;
  handleAnswer: (answer: string | UserAnswer) => void;
  setCurrent: React.Dispatch<React.SetStateAction<number>>;
  setFinished: React.Dispatch<React.SetStateAction<boolean>>;
  retryLoadQuestions: () => void;
}

export function useSinglePlayerQuizController({
  category: _category,
  categoryId,
  questionCount,
  difficulty_id,
  onQuizEnd,
  initialQuestions,
  sessionId: _sessionId,
}: UseSinglePlayerQuizControllerArgs): UseSinglePlayerQuizControllerResult & {
  showFeedback: boolean;
  timeLeft: number;
  maxTime: number;
  handleNextQuestion: () => void;
  setShowFeedback: React.Dispatch<React.SetStateAction<boolean>>;
} {
  const [questions, setQuestions] = useState<QuizQuestion[]>(initialQuestions || []);
  const [current, setCurrent] = useState<number>(0);
  const [answers, setAnswers] = useState<StoredSinglePlayerAnswer[]>([]);
  const [questionOutcomes, setQuestionOutcomes] = useState<QuestionOutcome[]>([]);
  const quizStartedAtRef = useRef<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [score, setScore] = useState<number>(0);
  const [correctCount, setCorrectCount] = useState<number>(0);
  const [finished, setFinished] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const feedbackRevealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRevealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxTime = QUESTION_TIME_LIMIT;
  const [timeLeft, setTimeLeft] = useState<number>(maxTime);
  
  // Get user's current language for question fetching
  const userLanguage = useLanguageStore((state) => state.language);

  // Track user ID for analytics
  const [userId, setUserId] = useState<string | null>(null);

  // Conversion funnel tracking with user ID
  const { trackQuizCompletion } = useConversionFunnel(userId);

  // Extracted loadQuestions as a reusable async function for initial load and retry
  const loadQuestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Start a new quiz session
      const user = await supabase.auth.getUser();
      const fetchedUserId = user?.data?.user?.id;

      // Set userId for analytics
      if (fetchedUserId) {
        setUserId(fetchedUserId);
      }

      if (fetchedUserId && !initialQuestions) {
        // Start session tracking
        await sessionQuestionService.startSession(
          fetchedUserId,
          categoryId,
          Number(difficulty_id) || 1,
          questionCount,
          false, // Not multiplayer
          undefined
        );
      }

      let fetchedQuestions = initialQuestions;
      if (!initialQuestions) {
        setShowFeedback(false);
        setTimeLeft(maxTime);
        const res = await fetchQuestions({
          categoryId,
          difficultyId: difficulty_id,
          limit: questionCount,
          languageCode: userLanguage,
          useSmartSelection: true,
          userId: fetchedUserId || null
        });
        fetchedQuestions = res || [];
      }

      // Normalize options: ensure each option is an object with {id, text}
      const normalizedQuestions = (fetchedQuestions ?? []).map((q: QuizQuestion) => ({
        ...q,
        options: (q.options ?? []).map((opt: QuizOption, idx: number) =>
          typeof opt === 'string' ? { id: String(idx), text: opt } : opt
        ),
      }));
      setQuestions(normalizedQuestions as QuizQuestion[]);
      quizStartedAtRef.current = Date.now();

      // If no questions were found after all retries in fetchQuestions, set an error
      if (normalizedQuestions.length === 0 && !initialQuestions) {
        setError('No questions available for this category. Tap Retry to try again.');
      }
    } catch (e) {
      const errorMsg = (e instanceof Error) ? e.message : "Failed to load questions";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [categoryId, difficulty_id, questionCount, userLanguage, initialQuestions, maxTime]);

  // Retry function - resets state and re-fetches questions
  const retryLoadQuestions = useCallback(() => {
    console.log('[useSinglePlayerQuizController] Manual retry triggered');
    setQuestions([]);
    setCurrent(0);
    setScore(0);
    setCorrectCount(0);
    setAnswers([]);
    setFinished(false);
    setError(null);
    loadQuestions();
  }, [loadQuestions]);

  // Initial load
  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleAnswer = useCallback(
    async (answer: string | UserAnswer) => {
      if (finished || showFeedback) {
        return;
      }
      
      const currentQuestion = questions[current];
      if (!currentQuestion) return;

      const startTime = Date.now();
      const answerOutcome = evaluateSinglePlayerAnswer(currentQuestion, answer);
      const answerToStore = normalizeStoredSinglePlayerAnswer(answer);

      setAnswers((prev: StoredSinglePlayerAnswer[]) => {
        const newAnswers = [...prev];
        newAnswers[current] = answerToStore;
        return newAnswers;
      });
      setQuestionOutcomes((prev) => {
        const next = [...prev];
        next[current] = { question_type: currentQuestion.question_type, isCorrect: answerOutcome.isCorrect };
        return next;
      });
      if (quizStartedAtRef.current === null) quizStartedAtRef.current = startTime;
      
      setScore((s) => s + answerOutcome.score);
      if (answerOutcome.isCorrect) {
        setCorrectCount((count) => count + 1);
      }
      
      if (feedbackRevealTimerRef.current) {
        clearTimeout(feedbackRevealTimerRef.current);
      }

      feedbackRevealTimerRef.current = setTimeout(() => {
        setShowFeedback(true);
        feedbackRevealTimerRef.current = null;
      }, 900);

      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      void sessionQuestionService.recordAnswer(
        currentQuestion.id,
        answerOutcome.isCorrect,
        timeSpent,
        answerOutcome.score,
        currentQuestion.question_type
      ).catch((error) => {
        console.error('Failed to record answer to history:', error);
      });
    },
    [current, finished, questions, showFeedback],
  );

  useEffect(() => () => {
    if (feedbackRevealTimerRef.current) {
      clearTimeout(feedbackRevealTimerRef.current);
    }
    if (timeoutRevealTimerRef.current) {
      clearTimeout(timeoutRevealTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (finished || questions.length === 0 || loading) {
      return;
    }

    if (feedbackRevealTimerRef.current) {
      clearTimeout(feedbackRevealTimerRef.current);
      feedbackRevealTimerRef.current = null;
    }
    if (timeoutRevealTimerRef.current) {
      clearTimeout(timeoutRevealTimerRef.current);
      timeoutRevealTimerRef.current = null;
    }
    setTimeLeft(maxTime);
    setShowFeedback(false);

  }, [current, finished, questions.length, loading]);

  useEffect(() => {
    if (finished || questions.length === 0 || loading || showFeedback || answers[current] !== undefined) {
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((remaining) => Math.max(0, remaining - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [answers, current, finished, loading, questions.length, showFeedback]);

  useEffect(() => {
    if (timeLeft > 0 || finished || questions.length === 0 || loading || showFeedback || answers[current] !== undefined) {
      return;
    }

    const currentQuestion = questions[current];
    if (!currentQuestion) return;

    setAnswers((prev: StoredSinglePlayerAnswer[]) => {
      if (prev[current] !== undefined) return prev;
      const newAnswers = [...prev];
      newAnswers[current] = '__timeout__';
      return newAnswers;
    });

    if (timeoutRevealTimerRef.current) {
      clearTimeout(timeoutRevealTimerRef.current);
    }

    timeoutRevealTimerRef.current = setTimeout(() => {
      setShowFeedback(true);
      timeoutRevealTimerRef.current = null;
    }, 900);

    void sessionQuestionService.recordAnswer(
      currentQuestion.id,
      false,
      maxTime,
      0,
      currentQuestion.question_type
    ).catch((error) => {
      console.error('Failed to record timed-out answer to history:', error);
    });
  }, [answers, current, finished, loading, maxTime, questions, showFeedback, timeLeft]);

  const handleNextQuestion = useCallback(async () => {
    if (!showFeedback) return;
    if (current + 1 < questions.length) {
      setCurrent((c) => c + 1);
      setShowFeedback(false);
      setTimeLeft(maxTime);
    } else {
      setFinished(true);
      
      // Complete the quiz session
      try {
        const sessionStats = sessionQuestionService.getSessionStats();
        await sessionQuestionService.completeSession(
          sessionStats.correctAnswers,
          score,
          Math.floor(sessionStats.averageTime * questions.length)
        );
      } catch (error) {
        console.error('Failed to complete session:', error);
      }
      
      // Track quiz completion for conversion funnel
      try {
        await trackQuizCompletion(`quiz_${categoryId}`, score);
      } catch (error) {
        console.error('Failed to track quiz completion:', error);
      }
      
      if (onQuizEnd) onQuizEnd({ score: correctCount, total: questions.length });
    }
  }, [showFeedback, current, questions.length, setCurrent, setShowFeedback, setTimeLeft, setFinished, correctCount, score, onQuizEnd, trackQuizCompletion, categoryId]);

  const currentQuestion = questions[current];

  const getElapsedSeconds = useCallback(
    () => (quizStartedAtRef.current === null ? null : Math.round((Date.now() - quizStartedAtRef.current) / 1000)),
    [],
  );

  return {
    questions,
    questionOutcomes,
    getElapsedSeconds,
    currentQuestion,
    currentIndex: current,
    totalQuestions: questions.length,
    answers,
    loading,
    score,
    correctCount,
    finished,
    error,
    handleAnswer,
    setCurrent,
    setFinished,
    retryLoadQuestions,
    showFeedback,
    timeLeft,
    maxTime,
    handleNextQuestion,
    setShowFeedback,
  };
}
